import React, { useState, useEffect } from "react";
import { FiPlus } from "react-icons/fi";
import { FaX } from "react-icons/fa6";
import { Country, State, City } from "country-state-city"; // Package Import

import { baseurl } from "../../baseurl";
import axios from "axios";
import Swal from "sweetalert2";

function AddCustomer({ onCustomerAdded }) {
  const token = localStorage.getItem("auth_token");

  /* ---------------- STATES ---------------- */
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [newCustomer, setNewCustomer] = useState({
    name: "",
    email: "",
    phone: "",
    gstin: "",
    state: "",      // Stores State Code (e.g., 'MH')
    city: "",       // Stores City Name
    country: "IN",  // Default to India
    zip_code: "",
    address: "",
  });

  /* ---------------- DATA HELPERS ---------------- */
  const countries = Country.getAllCountries();
  const states = newCustomer.country ? State.getStatesOfCountry(newCustomer.country) : [];
  const cities = (newCustomer.country && newCustomer.state) 
    ? City.getCitiesOfState(newCustomer.country, newCustomer.state) 
    : [];

  /* ---------------- TOAST ---------------- */
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 2500,
    timerProgressBar: true,
  });

  /* ---------------- INPUT HANDLER ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    // ✅ Phone logic
    if (name === "phone") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setNewCustomer((prev) => ({ ...prev, phone: digitsOnly }));
      return;
    }

    // ✅ Location logic: Reset child selections when parent changes
    if (name === "country") {
      setNewCustomer(prev => ({ ...prev, country: value, state: "", city: "" }));
    } else if (name === "state") {
      setNewCustomer(prev => ({ ...prev, state: value, city: "" }));
    } else {
      setNewCustomer((prev) => ({ ...prev, [name]: value }));
    }
  };

  /* ---------------- ADD CUSTOMER ---------------- */
  const handleAddCustomer = async () => {
    /* ✅ Phone validation */
    if (!newCustomer.phone || newCustomer.phone.length !== 10) {
      Toast.fire({
        icon: "error",
        title: "Phone number must be exactly 10 digits",
      });
      return;
    }

    try {
      setSaving(true);

      // Extract full names from codes for the API payload
      const selectedCountryObj = Country.getCountryByCode(newCustomer.country);
      const selectedStateObj = State.getStateByCodeAndCountry(newCustomer.state, newCustomer.country);

      const payload = {
        name: newCustomer.name,
        email: newCustomer.email,
        phone: newCustomer.phone,
        gstin: newCustomer.gstin,
        state: selectedStateObj ? selectedStateObj.name : newCustomer.state,
        city: newCustomer.city,
        country: selectedCountryObj ? selectedCountryObj.name : newCustomer.country,
        zip_code: newCustomer.zip_code,
        address: newCustomer.address,
      };

      await axios.post(`${baseurl}addCustomer`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      Toast.fire({
        icon: "success",
        title: "Customer added successfully",
      });

      setShowModal(false);

      if (onCustomerAdded) {
        onCustomerAdded();
      }

      setNewCustomer({
        name: "", email: "", phone: "", gstin: "",
        state: "", city: "", country: "IN", zip_code: "", address: "",
      });

    } catch (err) {
      Toast.fire({
        icon: "error",
        title: err?.response?.data?.message || "Failed to add customer",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        className="btn btn-primary d-flex align-items-center"
        onClick={() => setShowModal(true)}
      >
        <FiPlus className="me-2" /> Add Customer
      </button>

      {showModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-lg modal-dialog-centered">
              <div className="modal-content">

                <div className="modal-header bg-one">
                  <h5 className="modal-title">Add Customer</h5>
                  <button
                    className="btn btn-sm"
                    onClick={() => setShowModal(false)}
                  >
                    <FaX className="text-white" />
                  </button>
                </div>

                <div className="modal-body">
                  <div className="row g-3">

                    {/* BASIC FIELDS */}
                    {["name", "email", "phone", "gstin"].map((f) => (
                      <div className="col-md-6" key={f}>
                        <label className="form-label text-capitalize">{f === "gstin" ? "GSTIN" : f}</label>
                        <input
                          className="form-control"
                          name={f} placeholder={`Enter ${f}`}
                          value={newCustomer[f]}
                          onChange={handleChange}
                          {...(f === "phone" && {
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                            maxLength: 10,
                            placeholder: "10-digit phone number",
                          })}
                        />
                      </div>
                    ))}

                    {/* COUNTRY SELECT */}
                    <div className="col-md-6">
                      <label className="form-label">Country</label>
                      <select 
                        className="form-select" 
                        name="country" 
                        value={newCustomer.country} 
                        onChange={handleChange}
                      >
                        <option value="">Select Country</option>
                        {countries.map((c) => (
                          <option key={c.isoCode} value={c.isoCode}>{c.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* STATE SELECT */}
                    <div className="col-md-6">
                      <label className="form-label">State</label>
                      <select 
                        className="form-select" 
                        name="state" 
                        value={newCustomer.state} 
                        onChange={handleChange}
                        disabled={!newCustomer.country}
                      >
                        <option value="">Select State</option>
                        {states.map((s) => (
                          <option key={s.isoCode} value={s.isoCode}>{s.name}</option>
                        ))}
                      </select>
                    </div>

                    {/* CITY SELECT */}
                    <div className="col-md-6">
                      <label className="form-label">City</label>
                      <select 
                        className="form-select" 
                        name="city" 
                        value={newCustomer.city} 
                        onChange={handleChange}
                        disabled={!newCustomer.state}
                      >
                        <option value="">Select City</option>
                        {cities.map((ci) => (
                          <option key={ci.name} value={ci.name}>{ci.name}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6">
                      <label className="form-label">Zip Code</label>
                      <input
                        className="form-control"
                        name="zip_code"
                        placeholder="Enter zip code"
                        value={newCustomer.zip_code}
                        onChange={handleChange}
                      />
                    </div>

                    <div className="col-md-12">
                      <label className="form-label">Address</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        name="address"
                        placeholder="Enter full address"
                        value={newCustomer.address}
                        onChange={handleChange}
                      />
                    </div>

                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleAddCustomer}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Add Customer"}
                  </button>
                </div>

              </div>
            </div>
          </div>

          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}

export default AddCustomer;